import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Timetable() {
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";
  const [items, setItems] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [f, setF] = useState({ teacher_id: "", teacher_name: "", class_name: "1", subject: "", day: "Mon", period: "1", room: "" });

  const load = async () => {
    try {
      const a = await api.get("/assignments");
      setItems(a.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Load failed"); }
    if (!isTeacher) {
      // /users is admin-only; teachers skip it
      try {
        const u = await api.get("/users", { params: { role: "teacher" } });
        setTeachers(u.data);
      } catch { /* non-admin: no teacher list needed */ }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    try {
      const t = teachers.find((x) => x.id === f.teacher_id);
      await api.post("/assignments", { ...f, teacher_name: t?.name || f.teacher_name || "Teacher" });
      toast.success("Assigned (WhatsApp notification queued - MOCKED)");
      setF({ ...f, subject: "", room: "" }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/assignments/${id}`); load(); };

  const visible = isTeacher ? items.filter((x) => x.teacher_id === user?.id) : items;

  return (
    <DashLayout variant={isTeacher ? "teacher" : "school"} title={isTeacher ? "My Timetable" : "Timetable & Staff Assignments"}>
      {!isTeacher && (
        <>
          <div className="text-sm text-slate-500 mb-4">Assign teachers to class periods. Each save triggers a MOCKED WhatsApp notification to the teacher.</div>
          <Card className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end mb-6">
            <Select value={f.teacher_id} onValueChange={(v) => setF({ ...f, teacher_id: v })}>
              <SelectTrigger data-testid="asg-teacher"><SelectValue placeholder="Teacher" /></SelectTrigger>
              <SelectContent>{teachers.length === 0 && <SelectItem value="none" disabled>No teachers – add in Users</SelectItem>}{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Class" value={f.class_name} onChange={(e) => setF({ ...f, class_name: e.target.value })} data-testid="asg-class" />
            <Input placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="asg-subject" />
            <Select value={f.day} onValueChange={(v) => setF({ ...f, day: v })}>
              <SelectTrigger data-testid="asg-day"><SelectValue /></SelectTrigger>
              <SelectContent>{DAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Period" value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} data-testid="asg-period" />
            <Button onClick={add} className="bg-blue-700 hover:bg-blue-800" data-testid="asg-add"><Plus className="w-4 h-4 mr-1" />Assign</Button>
          </Card>
        </>
      )}

      <div className="bg-white border rounded-lg" data-testid="timetable-table">
        <Table><TableHeader><TableRow><TableHead>Teacher</TableHead><TableHead>Class</TableHead><TableHead>Subject</TableHead><TableHead>Day</TableHead><TableHead>Period</TableHead>{!isTeacher && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
          <TableBody>
            {visible.map(x => <TableRow key={x.id} data-testid={`row-asg-${x.id}`}><TableCell className="font-semibold">{x.teacher_name}</TableCell><TableCell>{x.class_name}</TableCell><TableCell>{x.subject}</TableCell><TableCell>{x.day}</TableCell><TableCell>{x.period}</TableCell>{!isTeacher && <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => del(x.id)} data-testid={`asg-del-${x.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>}</TableRow>)}
            {visible.length === 0 && <TableRow><TableCell colSpan={isTeacher ? 5 : 6} className="text-center py-8 text-slate-500" data-testid="timetable-empty">{isTeacher ? "No periods assigned to you yet" : "No assignments yet"}</TableCell></TableRow>}
          </TableBody></Table>
      </div>
    </DashLayout>
  );
}
